/**
 * SchedulePostModal Component
 * 
 * Modal/Bottom sheet for scheduling posts and managing scheduled posts.
 * - Date and time picker for scheduling (styled to match Planner app)
 * - List of pending scheduled posts with edit capability
 * - Edit/Cancel/Publish Now options
 */

import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useDesign } from '@/contexts/DesignContext';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Calendar as CalendarIcon,
  Clock,
  X,
  Loader2,
  Edit,
  Trash2,
  Send,
  CalendarClock,
  ChevronRight,
  Image as ImageIcon,
  Smile,
  Video,
  Play,
  BarChart3,
  Plus,
  Tag
} from 'lucide-react';
import RichTextEditor from '@/components/shared/RichTextEditor';
import UploadProgress from '@/components/shared/UploadProgress';
import { uploadWithProgress } from '@/lib/uploadVideoWithProgress';
import { VoiceMemoButton, GifPickerDialog } from './index';
import { IMAGE_ACCEPT } from '@/lib/imageFiles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get minimum date (today)
const getMinDate = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
};

// Helper to get default time (9:00 AM)
const getDefaultTime = () => '09:00';

// Generate time options in 15-minute intervals
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      const value = `${h}:${m}`;
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${m.padStart(2, '0')} ${ampm}`;
      options.push({ value, label });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// Format scheduled time for display in user's local timezone
const formatScheduledTime = (isoString) => {
  const date = new Date(isoString);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: userTimezone
  });
};

// Truncate content for preview
const truncateContent = (html, maxLength = 100) => {
  const text = html?.replace(/<[^>]+>/g, '').trim() || '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export function SchedulePostModal({ 
  isOpen, 
  onClose, 
  user,
  onSchedule,
  initialDateTime = null 
}) {
  const { isStudio } = useDesign();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(getDefaultTime());
  const [scheduling, setScheduling] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialDateTime) {
        const dt = new Date(initialDateTime);
        setSelectedDate(dt);
        setSelectedTime(format(dt, 'HH:mm'));
      } else {
        // Default to tomorrow
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setSelectedDate(tomorrow);
        setSelectedTime('09:00');
      }
    }
  }, [isOpen, initialDateTime]);

  const handleSchedule = async () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    // Combine date and time
    const scheduledDateTime = new Date(selectedDate);
    const [hours, minutes] = selectedTime.split(':').map(Number);
    scheduledDateTime.setHours(hours, minutes, 0, 0);

    if (scheduledDateTime <= new Date()) {
      toast.error('Please select a future date and time');
      return;
    }

    setScheduling(true);
    try {
      await onSchedule(scheduledDateTime.toISOString());
      onClose();
    } catch (err) {
      console.error('Schedule error:', err);
    } finally {
      setScheduling(false);
    }
  };

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Use Sheet on mobile, Dialog on desktop
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const content = (
    <div className="space-y-5 py-2">
      {/* Date Picker - Inline Calendar (Safari-compatible - avoids nested portals) */}
      <div className="space-y-2">
        <Label className={isStudio ? 'st-schedlbl' : "text-sm font-medium"}>Date</Label>
        <Button
          variant="outline"
          className={isStudio ? 'st-schedfield w-full justify-start text-left font-normal' : "w-full justify-start text-left font-normal h-11 rounded-xl"}
          data-testid="schedule-date-btn"
          onClick={() => setCalendarOpen(!calendarOpen)}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-slate-400 dark:text-slate-500" />
          {selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : 'Pick a date'}
        </Button>
        {calendarOpen && (
          <div className={isStudio ? 'border rounded-lg p-2 bg-card' : "border rounded-xl p-2 bg-card shadow-sm"}>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                setSelectedDate(date);
                setCalendarOpen(false);
              }}
              disabled={(date) => date < getMinDate()}
              initialFocus
            />
          </div>
        )}
      </div>

      {/* Time Picker */}
      <div className="space-y-2">
        <Label className={isStudio ? 'st-schedlbl' : "text-sm font-medium"}>Time</Label>
        <Select value={selectedTime} onValueChange={setSelectedTime}>
          <SelectTrigger className={isStudio ? 'st-schedfield' : "h-11 rounded-xl"} data-testid="schedule-time-input">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-400 dark:text-slate-500" />
              <SelectValue placeholder="Select time">
                {TIME_OPTIONS.find(t => t.value === selectedTime)?.label || 'Select time'}
              </SelectValue>
            </div>
          </SelectTrigger>
          <SelectContent className="max-h-[280px]">
            {TIME_OPTIONS.map((time) => (
              <SelectItem key={time.value} value={time.value}>
                {time.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Timezone: {userTimezone}
        </p>
      </div>

      {/* Preview */}
      {selectedDate && selectedTime && (
        <div className={isStudio ? 'st-schedprev' : "bg-indigo-50 dark:bg-indigo-950/40 rounded-xl p-3 border border-indigo-100"}>
          <p className={isStudio ? undefined : "text-sm text-indigo-700 dark:text-indigo-300 font-medium flex items-center gap-2"}>
            <CalendarClock className="w-4 h-4" />
            Will post on {format(selectedDate, 'MMM d')} at {format(new Date(`2000-01-01T${selectedTime}`), 'h:mm a')}
          </p>
        </div>
      )}

      <div className={isStudio ? 'st-schedfoot' : "flex gap-2 pt-2"}>
        <Button
          variant="outline"
          onClick={onClose}
          className={isStudio ? 'st-btn st-sec' : "flex-1 h-11 rounded-xl"}
          disabled={scheduling}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSchedule}
          className={isStudio ? 'st-btn st-pri gap-2' : "flex-1 gap-2 h-11 rounded-xl bg-indigo-500 hover:bg-indigo-600"}
          disabled={scheduling || !selectedDate}
          data-testid="confirm-schedule-btn"
        >
          {scheduling ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <CalendarClock className="w-4 h-4" />
          )}
          Schedule
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="bottom" className="rounded-t-xl">
          <SheetHeader className="pb-2">
            <SheetTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                <CalendarClock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <span>Schedule Post</span>
            </SheetTitle>
            <SheetDescription>
              Choose when to publish this post
            </SheetDescription>
          </SheetHeader>
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={isStudio ? 'st-scheddlg sm:max-w-md' : "sm:max-w-md"}>
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
              <CalendarClock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <span>Schedule Post</span>
          </DialogTitle>
          <DialogDescription>
            Choose when to publish this post
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}


// Helper to format voice memo duration
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Edit Scheduled Post Modal
export function EditScheduledPostModal({
  isOpen,
  onClose,
  post,
  user,
  onSave,
  onPublishNow
}) {
  const [content, setContent] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Media state
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedGif, setSelectedGif] = useState(null);
  const [voiceMemo, setVoiceMemo] = useState(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);

  // Poll state
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollAllowOther, setPollAllowOther] = useState(false);
  const [pollAllowMultiple, setPollAllowMultiple] = useState(false);

  // Group state
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const groupDropdownRef = useRef(null);
  
  const imageInputRef = useRef(null);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Load post data when modal opens
  useEffect(() => {
    if (isOpen && post) {
      setContent(post.content || '');
      const dt = new Date(post.scheduled_for);
      setSelectedDate(dt);
      setSelectedTime(format(dt, 'HH:mm'));

      // Load existing media
      setSelectedImages(post.media_urls?.map(url => ({ url })) || []);
      setSelectedGif(post.gif_url ? { url: post.gif_url } : null);
      setVoiceMemo(post.voice_memo_data ? { data: post.voice_memo_data, duration: 0, mimeType: post.voice_memo_mime || 'audio/webm' } : null);
      setYoutubeUrl(''); // YouTube URLs are embedded in content

      // Load existing poll
      if (post.poll) {
        setShowPollCreator(true);
        setPollQuestion(post.poll.question || '');
        setPollOptions(post.poll.options?.map(o => o.text) || ['', '']);
        setPollAllowOther(post.poll.allow_other || false);
        setPollAllowMultiple(post.poll.allow_multiple || false);
      } else {
        setShowPollCreator(false);
        setPollQuestion('');
        setPollOptions(['', '']);
        setPollAllowOther(false);
        setPollAllowMultiple(false);
      }

      // Load existing groups
      setSelectedGroups(post.groups || []);

      // Fetch all church groups for the picker
      fetch(`${API_URL}/api/groups?church_id=${user.church_id}`)
        .then(res => res.ok ? res.json() : [])
        .then(groups => setAvailableGroups(groups))
        .catch(() => setAvailableGroups([]));
    }
  }, [isOpen, post, user.church_id]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedImages([]);
      setSelectedGif(null);
      setVoiceMemo(null);
      setYoutubeUrl('');
      setShowGifPicker(false);
      setShowPollCreator(false);
      setPollQuestion('');
      setPollOptions(['', '']);
      setPollAllowOther(false);
      setPollAllowMultiple(false);
      setSelectedGroups([]);
      setAvailableGroups([]);
      setShowGroupDropdown(false);
    }
  }, [isOpen]);

  // Close group dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(e.target)) {
        setShowGroupDropdown(false);
      }
    };
    if (showGroupDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showGroupDropdown]);

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    if (selectedImages.length + files.length > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }

    // Validate file sizes (max 5MB each)
    const oversized = files.filter(f => f.size > 7 * 1024 * 1024); // R15: server cap is 7 MB
    if (oversized.length > 0) {
      toast.error('Image File Size too big. Must be under 7MB');
      return;
    }

    setUploadingImages(true);
    setImageUploadProgress(0);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      
      // Real XHR byte-accurate progress. 10% reserved for server-side
      // resize/optimize work after the bytes finish uploading.
      const data = await uploadWithProgress(
        `${API_URL}/api/comms/v3/upload/images?user_id=${encodeURIComponent(user.id)}`,
        formData,
        setImageUploadProgress,
        10,
      );
      // Endpoint returns { images: [{ url, filename, size }, ...], count: N }.
      // selectedImages renders `img.url` so we spread the image objects directly.
      // (Pre-existing bug: the old fetch-based code did `data.urls.map(url => ({url}))`
      // which referenced a field the endpoint never returned. Image attach on
      // scheduled posts was silently broken before this refactor.)
      setSelectedImages(prev => [...prev, ...(data.images || [])]);
    } catch (err) {
      toast.error(err?.message || 'Failed to upload images');
    } finally {
      setUploadingImages(false);
      setImageUploadProgress(0);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  // Check if we have valid content
  const hasPoll = pollQuestion.trim() && pollOptions.filter(o => o.trim()).length >= 2;
  const hasValidContent = content.replace(/<[^>]*>/g, '').trim() ||
    selectedImages.length > 0 ||
    selectedGif ||
    voiceMemo ||
    hasPoll;

  const handleSave = async () => {
    if (!hasValidContent) {
      toast.error('Please add some content');
      return;
    }

    // Combine date and time
    const scheduledDateTime = new Date(selectedDate);
    const [hours, minutes] = selectedTime.split(':').map(Number);
    scheduledDateTime.setHours(hours, minutes, 0, 0);

    if (scheduledDateTime <= new Date()) {
      toast.error('Please select a future date and time');
      return;
    }

    setSaving(true);
    try {
      // Build poll data if present
      const pollData = hasPoll ? {
        question: pollQuestion.trim(),
        options: pollOptions.filter(o => o.trim()).map(o => ({ text: o.trim(), votes: [] })),
        allow_other: pollAllowOther,
        allow_multiple: pollAllowMultiple
      } : null;

      const response = await fetch(
        `${API_URL}/api/comms/v3/scheduled-posts/${post.id}?user_id=${user.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: content,
            media_urls: selectedImages.map(img => img.url),
            gif_url: selectedGif?.url || null,
            voice_memo_data: voiceMemo?.data || null,
            voice_memo_mime: voiceMemo?.mimeType || null,
            voice_memo_duration: voiceMemo?.duration || null,
            poll: pollData,
            scheduled_for: scheduledDateTime.toISOString(),
            group_ids: selectedGroups.map(g => g.id)
          })
        }
      );

      if (response.ok) {
        toast.success('Scheduled post updated!');
        onSave();
        onClose();
      } else {
        const error = await response.json();
        toast.error('Couldn\'t update this scheduled post. Please try again.');
      }
    } catch (err) {
      toast.error('Couldn\'t update this scheduled post. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishNow = async () => {
    setPublishing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/comms/v3/scheduled-posts/${post.id}/publish-now?user_id=${user.id}`,
        { method: 'POST' }
      );

      if (response.ok) {
        toast.success('Post published!');
        onPublishNow();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to publish');
      }
    } catch (err) {
      toast.error('Failed to publish post');
    } finally {
      setPublishing(false);
    }
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const editContent = (
    <div className="space-y-4 py-2">
      {/* Hidden file input */}
      <input
        type="file"
        ref={imageInputRef}
        className="hidden"
        accept={IMAGE_ACCEPT}
        multiple
        onChange={handleImageUpload}
      />

      {/* Rich Text Editor */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Post Content</Label>
        <RichTextEditor
          content={content}
          onChange={setContent}
          onSubmit={handleSave}
          placeholder="What's on your mind?"
          minHeight="100px"
        />
      </div>

      {/* Media Toolbar */}
      <div className="flex items-center gap-1 py-1">
        <Button 
          variant="ghost" 
          size="sm" 
          className={`text-slate-500 dark:text-slate-400 h-8 ${uploadingImages ? 'px-2 opacity-80' : 'px-2'}`}
          onClick={() => imageInputRef.current?.click()}
          disabled={uploadingImages || selectedImages.length >= 10}
          title={!uploadingImages ? 'Add image' : imageUploadProgress >= 90 && imageUploadProgress < 100 ? 'Processing image…' : `Uploading ${imageUploadProgress}%`}
        >
          {uploadingImages ? (
            <UploadProgress progress={imageUploadProgress} size={16} className="text-blue-500" />
          ) : (
            <ImageIcon className="w-4 h-4" />
          )}
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-slate-500 dark:text-slate-400 h-8 px-2"
          onClick={() => setShowGifPicker(true)}
        >
          <Smile className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-slate-500 dark:text-slate-400 h-8 px-2"
          onClick={() => {
            const url = prompt('Enter YouTube or Loom video URL:');
            if (url) setYoutubeUrl(url);
          }}
        >
          <Video className="w-4 h-4" />
        </Button>
        <VoiceMemoButton
          onVoiceMemo={(data, duration, mimeType) => setVoiceMemo({ data, duration, mimeType: mimeType || 'audio/mp4' })}
          disabled={!!voiceMemo}
          userId={user.id}
        />
        <Button
          variant="ghost"
          size="sm"
          className={`text-slate-500 dark:text-slate-400 h-8 px-2 ${showPollCreator ? 'text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40' : ''}`}
          onClick={() => setShowPollCreator(!showPollCreator)}
          data-testid="scheduled-poll-btn"
        >
          <BarChart3 className="w-4 h-4" />
        </Button>
      </div>

      {/* Poll Creator UI */}
      {showPollCreator && (
        <div className="bg-slate-50 dark:bg-[#1e1f22] rounded-xl p-3 space-y-3 border border-slate-200 dark:border-white/10">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-500 dark:text-blue-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Create Poll</span>
            </div>
            <button onClick={() => setShowPollCreator(false)} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200">
              <X className="w-4 h-4" />
            </button>
          </div>
          <input
            type="text"
            placeholder="Ask a question..."
            className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            value={pollQuestion}
            onChange={(e) => setPollQuestion(e.target.value)}
            data-testid="scheduled-poll-question-input"
          />
          <div className="space-y-2">
            {pollOptions.map((option, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  placeholder={`Option ${index + 1}`}
                  className="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  value={option}
                  onChange={(e) => {
                    const newOptions = [...pollOptions];
                    newOptions[index] = e.target.value;
                    setPollOptions(newOptions);
                  }}
                  data-testid={`scheduled-poll-option-${index}`}
                />
                {pollOptions.length > 2 && (
                  <button
                    onClick={() => setPollOptions(pollOptions.filter((_, i) => i !== index))}
                    className="text-slate-400 dark:text-slate-500 hover:text-red-500 p-2"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {pollOptions.length < 5 && (
              <button
                onClick={() => setPollOptions([...pollOptions, ''])}
                className="text-blue-500 dark:text-blue-400 text-sm flex items-center gap-1 hover:text-blue-600"
              >
                <Plus className="w-4 h-4" /> Add option
              </button>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={pollAllowOther}
              onChange={(e) => setPollAllowOther(e.target.checked)}
              className="rounded"
              data-testid="scheduled-poll-allow-other"
            />
            Allow &quot;Other&quot; option
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={pollAllowMultiple}
              onChange={(e) => setPollAllowMultiple(e.target.checked)}
              className="rounded"
              data-testid="scheduled-poll-allow-multiple"
            />
            Allow multiple selections
          </label>
        </div>
      )}

      {/* Group Tags — Admin/Owner only */}
      {(user?.role === 'admin' || user?.role === 'owner') && (selectedGroups.length > 0 || availableGroups.length > 0) && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-slate-400 dark:text-slate-500" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Group Tags</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {selectedGroups.map(g => {
              const color = g.color || '#6366F1';
              return (
                <span
                  key={g.id}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`, color: `color-mix(in srgb, ${color} 70%, var(--badge-text-mix))`, border: `1px solid color-mix(in srgb, ${color} 20%, transparent)` }}
                >
                  @{g.tag}
                  <button
                    onClick={() => setSelectedGroups(prev => prev.filter(sg => sg.id !== g.id))}
                    className="ml-0.5 hover:opacity-60"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
            {/* Add group button + dropdown */}
            {availableGroups.filter(ag => !selectedGroups.some(sg => sg.id === ag.id)).length > 0 && (
              <div className="relative" ref={groupDropdownRef}>
                <button
                  onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-[#3a3d41] hover:bg-slate-200 transition-colors"
                  data-testid="edit-add-group-btn"
                >
                  <Plus className="w-3 h-3" /> Add Group
                </button>
                {showGroupDropdown && (
                  <div className="absolute left-0 top-full mt-1 bg-card border border-slate-200 dark:border-white/10 rounded-lg shadow-lg z-50 py-1 min-w-[180px] max-h-[160px] overflow-y-auto">
                    {availableGroups
                      .filter(ag => !selectedGroups.some(sg => sg.id === ag.id))
                      .map(g => (
                        <button
                          key={g.id}
                          onClick={() => {
                            setSelectedGroups(prev => [...prev, { id: g.id, name: g.name, tag: g.tag, color: g.color }]);
                            setShowGroupDropdown(false);
                          }}
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-white/5 flex items-center gap-2"
                        >
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: g.color || '#6366F1' }} />
                          @{g.tag}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {selectedGroups.length > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">Only members of these groups will see this post</p>
          )}
        </div>
      )}

      {/* Selected Images Preview */}
      {selectedImages.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedImages.map((img, index) => (
            <div key={index} className="relative">
              <img 
                src={img.url} 
                alt={`Selected ${index + 1}`} 
                className="w-16 h-16 object-cover rounded-lg"
              />
              <button
                onClick={() => setSelectedImages(prev => prev.filter((_, i) => i !== index))}
                className="absolute -top-1 -right-1 w-5 h-5 bg-slate-800 rounded-full flex items-center justify-center text-white"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Selected GIF Preview */}
      {selectedGif && (
        <div className="relative inline-block">
          <img src={selectedGif.url} alt="Selected GIF" className="rounded-lg max-h-24" />
          <button
            onClick={() => setSelectedGif(null)}
            className="absolute -top-2 -right-2 w-6 h-6 bg-slate-800 rounded-full flex items-center justify-center text-white"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* YouTube/Loom URL preview */}
      {youtubeUrl && (
        <div className="flex items-center gap-2 p-2 bg-slate-100 dark:bg-[#3a3d41] rounded-lg">
          <div className={`w-8 h-8 ${youtubeUrl.includes('loom.com') ? 'bg-purple-600' : 'bg-red-600'} rounded flex items-center justify-center`}>
            <Play className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
              {youtubeUrl.includes('loom.com') ? 'Loom Video' : 'YouTube Video'}
            </span>
            <p className="text-sm text-slate-600 dark:text-slate-300 truncate">{youtubeUrl}</p>
          </div>
          <button onClick={() => setYoutubeUrl('')} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Voice Memo Preview */}
      {voiceMemo && (
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:to-indigo-950/40 rounded-lg border border-blue-100">
          <button 
            onClick={() => {
              const isUrl = voiceMemo.data && voiceMemo.data.startsWith('http');
              const src = isUrl ? voiceMemo.data : `data:${voiceMemo.mimeType || 'audio/webm'};base64,${voiceMemo.data}`;
              const audio = new Audio(src);
              audio.play().catch(() => {
                if (!isUrl) {
                  const fallbackMime = (voiceMemo.mimeType || '').includes('webm') ? 'audio/mp4' : 'audio/webm';
                  const audio2 = new Audio(`data:${fallbackMime};base64,${voiceMemo.data}`);
                  audio2.play();
                }
              });
            }}
            className="w-10 h-10 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
            title="Play preview"
          >
            <Play className="w-5 h-5 text-white ml-0.5" />
          </button>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Voice Memo</span>
            {voiceMemo.duration > 0 && (
              <p className="text-xs text-blue-500 dark:text-blue-400">{formatDuration(voiceMemo.duration)}</p>
            )}
          </div>
          <button 
            onClick={() => setVoiceMemo(null)} 
            className="text-blue-400 hover:text-red-500 p-1.5 hover:bg-red-50 dark:hover:bg-red-500/15 rounded-full transition-colors"
            title="Remove voice memo"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Date Picker - Inline Calendar (Safari-compatible - avoids nested portals) */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Scheduled Date</Label>
        <Button
          variant="outline"
          className="w-full justify-start text-left font-normal h-11 rounded-xl"
          data-testid="edit-schedule-date-btn"
          onClick={() => setCalendarOpen(!calendarOpen)}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-slate-400 dark:text-slate-500" />
          {selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : 'Pick a date'}
        </Button>
        {calendarOpen && (
          <div className="border rounded-xl p-2 bg-card shadow-sm">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                setSelectedDate(date);
                setCalendarOpen(false);
              }}
              disabled={(date) => date < getMinDate()}
              initialFocus
            />
          </div>
        )}
      </div>

      {/* Time Picker */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Time</Label>
        <Select value={selectedTime} onValueChange={setSelectedTime}>
          <SelectTrigger className="h-11 rounded-xl">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-400 dark:text-slate-500" />
              <SelectValue placeholder="Select time">
                {TIME_OPTIONS.find(t => t.value === selectedTime)?.label || 'Select time'}
              </SelectValue>
            </div>
          </SelectTrigger>
          <SelectContent className="max-h-[280px]">
            {TIME_OPTIONS.map((time) => (
              <SelectItem key={time.value} value={time.value}>
                {time.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Timezone: {userTimezone}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 pt-2">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 h-11 rounded-xl"
            disabled={saving || publishing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1 gap-2 h-11 rounded-xl bg-indigo-500 hover:bg-indigo-600"
            disabled={saving || publishing || !hasValidContent}
            data-testid="save-scheduled-post-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save Changes
          </Button>
        </div>
        <Button
          variant="outline"
          onClick={handlePublishNow}
          className="w-full h-11 rounded-xl border-green-200 dark:border-green-900 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-500/15"
          disabled={saving || publishing}
          data-testid="publish-now-btn"
        >
          {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Publish Now
        </Button>
      </div>

      {/* GIF Picker Dialog */}
      <GifPickerDialog 
        open={showGifPicker} 
        onOpenChange={setShowGifPicker}
        onSelect={(gif) => { setSelectedGif(gif); setShowGifPicker(false); }}
      />
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="bottom" className="rounded-t-xl h-[85vh] flex flex-col">
          <SheetHeader className="pb-2 flex-shrink-0">
            <SheetTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                <Edit className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <span>Edit Scheduled Post</span>
            </SheetTitle>
            <SheetDescription>
              Update content or reschedule
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className="flex-1 min-h-0">
            {editContent}
          </ScrollArea>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader className="pb-2 flex-shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
              <Edit className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <span>Edit Scheduled Post</span>
          </DialogTitle>
          <DialogDescription>
            Update content or reschedule
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto pr-2">
          {editContent}
        </div>
      </DialogContent>
    </Dialog>
  );
}


export function ScheduledPostsPanel({ 
  isOpen, 
  onClose, 
  user 
}) {
  const { isStudio } = useDesign();
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [editingPost, setEditingPost] = useState(null);

  const fetchScheduledPosts = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/comms/v3/scheduled-posts?user_id=${user.id}&church_id=${user.church_id}`
      );
      if (response.ok) {
        const data = await response.json();
        setScheduledPosts(data.scheduled_posts || []);
      }
    } catch (err) {
      console.error('Error fetching scheduled posts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      fetchScheduledPosts();
    }
  }, [isOpen, user.id, user.church_id]);

  const handleCancel = async (postId) => {
    setActionLoading(postId);
    try {
      const response = await fetch(
        `${API_URL}/api/comms/v3/scheduled-posts/${postId}?user_id=${user.id}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        toast.success('Scheduled post cancelled');
        setScheduledPosts(prev => prev.filter(p => p.id !== postId));
      } else {
        const error = await response.json();
        toast.error('Couldn\'t cancel this post. Please try again.');
      }
    } catch (err) {
      toast.error('Couldn\'t cancel this post. Please check your connection and try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Flex column so the list takes whatever height the sheet/dialog leaves it
  // (capped at 400px) and scrolls inside, instead of overflowing the frame.
  const content = (
    <div className="py-2 flex-1 min-h-0 flex flex-col">
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400 dark:text-slate-500" />
        </div>
      ) : scheduledPosts.length === 0 ? (
        <div className={isStudio ? 'st-fdempty' : "text-center py-8 px-4"}>
          <div className={isStudio ? 'st-fdglyph' : "w-14 h-14 rounded-2xl bg-slate-100 dark:bg-[#3a3d41] flex items-center justify-center mx-auto mb-3"}>
            <CalendarClock className={isStudio ? undefined : "w-6 h-6 text-slate-400 dark:text-slate-500"} />
          </div>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">No scheduled posts</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
            Posts you schedule will appear here
          </p>
        </div>
      ) : (
        <ScrollArea className="flex-1 min-h-0 max-h-[400px]" data-testid="scheduled-posts-list">
          <div className="space-y-3 pr-2">
            {scheduledPosts.map((post) => (
              <Card key={post.id} className={isStudio ? 'st-schedcard border rounded-xl overflow-hidden' : "border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden"}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className={isStudio ? 'st-schedwhen' : "flex items-center gap-2 text-xs text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-950/40 px-2 py-1 rounded-lg"}>
                      <CalendarClock className="w-3.5 h-3.5" />
                      {formatScheduledTime(post.scheduled_for)}
                    </div>
                  </div>
                  
                  <p className="text-sm text-slate-700 dark:text-slate-200 mb-3 line-clamp-2">
                    {truncateContent(post.content)}
                  </p>
                  
                  {/* Group tags indicator */}
                  {post.groups && post.groups.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2" data-testid={`scheduled-post-groups-${post.id}`}>
                      {post.groups.map(g => {
                        const color = g.color || '#6366F1';
                        return (
                          <span
                            key={g.id}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`, color: `color-mix(in srgb, ${color} 70%, var(--badge-text-mix))`, border: `1px solid color-mix(in srgb, ${color} 20%, transparent)` }}
                          >
                            @{g.tag}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Attachments indicator */}
                  {(post.media_urls?.length > 0 || post.gif_url || post.poll || post.voice_memo_data) && (
                    <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500 mb-3">
                      {post.media_urls?.length > 0 && (
                        <span className="bg-slate-100 dark:bg-[#3a3d41] px-2 py-0.5 rounded">{post.media_urls.length} image(s)</span>
                      )}
                      {post.gif_url && <span className="bg-slate-100 dark:bg-[#3a3d41] px-2 py-0.5 rounded">GIF</span>}
                      {post.poll && <span className="bg-slate-100 dark:bg-[#3a3d41] px-2 py-0.5 rounded">Poll</span>}
                      {post.voice_memo_data && <span className="bg-slate-100 dark:bg-[#3a3d41] px-2 py-0.5 rounded">Voice</span>}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 h-9 text-xs rounded-lg"
                      onClick={() => setEditingPost(post)}
                      disabled={actionLoading === post.id}
                      data-testid={`edit-scheduled-${post.id}`}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9 text-xs rounded-lg text-red-500 dark:text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/15"
                      onClick={() => handleCancel(post.id)}
                      disabled={actionLoading === post.id}
                    >
                      {actionLoading === post.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Trash2 className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Edit Modal */}
      <EditScheduledPostModal
        isOpen={!!editingPost}
        onClose={() => setEditingPost(null)}
        post={editingPost}
        user={user}
        onSave={() => {
          fetchScheduledPosts();
          setEditingPost(null);
        }}
        onPublishNow={() => {
          fetchScheduledPosts();
          setEditingPost(null);
        }}
      />
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="bottom" className="rounded-t-xl h-[70vh] flex flex-col">
          <SheetHeader className="pb-2 flex-shrink-0">
            <SheetTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                <Clock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <span>Scheduled Posts</span>
            </SheetTitle>
            <SheetDescription>
              {scheduledPosts.length} post{scheduledPosts.length !== 1 ? 's' : ''} scheduled
            </SheetDescription>
          </SheetHeader>
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={isStudio ? 'st-schedpanel sm:max-w-lg max-h-[85vh] flex flex-col' : "sm:max-w-lg max-h-[85vh] flex flex-col"}>
        <DialogHeader className="pb-2 flex-shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <div className={isStudio ? 'st-schedglyph' : "w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center"}>
              <Clock className={isStudio ? undefined : "w-5 h-5 text-indigo-600 dark:text-indigo-400"} />
            </div>
            <span>Scheduled Posts</span>
          </DialogTitle>
          <DialogDescription>
            {scheduledPosts.length} post{scheduledPosts.length !== 1 ? 's' : ''} scheduled
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}

export default SchedulePostModal;
